/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, ElementRef, Inject, Input, OnChanges, ViewChild} from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import * as Highcharts from 'highcharts';

import template from './burn-rate-chart.component.html';
import {BURN_RATE_FIELDS} from '../options/chart-options';
import {AppLocaleService} from '../locale/app-locale.service';
import {getIntlLocaleData} from '../locale/intl-locale-data';
import {applyHighchartsLocale} from '../../workspace/widget/workspace-widget-chart-commons';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';

@Component({
  standalone: false,
  selector: 'adn-burn-rate-chart',
  template
})
export class BurnRateChartComponent implements OnChanges {
  @Input() burnRateData: any[] | null = null;

  @ViewChild('chartContainer')
  set chartContainer(chartContainer: ElementRef<HTMLElement>) {
    this.chartContainerRef = chartContainer;
    this.renderChart();
  }

  haveStats = false;
  error = false;
  isPending = false;
  noData = false;

  private chart: Highcharts.Chart | null = null;
  private chartContainerRef: ElementRef<HTMLElement> | null = null;
  private chartOptions: any = null;

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly appLocale: AppLocaleService
  ) {
    const timezone = this.LocalNetworkProfile.get('timeZone');
    const networkTimezoneOffset = moment.tz(timezone).utcOffset();

    applyHighchartsLocale(getIntlLocaleData(this.appLocale.getLocaleId()), networkTimezoneOffset);
  }

  ngOnChanges(): void {
    this.updateChartState();
  }

  private updateChartState(): void {
    if (this.burnRateData === null || this.burnRateData === undefined) {
      this.error = false;
      this.haveStats = false;
      this.noData = false;
      this.chartOptions = null;
      this.destroyChart();
      return;
    }

    const statsSeries = _.map([BURN_RATE_FIELDS.burnRate, BURN_RATE_FIELDS.frequencyCapping], (series) => {
      return {
        type: 'line' as const,
        id: series.id,
        name: series.name,
        data: _.map(this.burnRateData, (data) => {
          return {
            x: moment(data.time).valueOf(),
            y: _.get(data, series.id) * 100
          };
        }),
        yAxis: series.yAxis,
        tooltip: series.tooltip
      };
    });

    this.error = false;
    this.haveStats = true;
    this.noData = this.burnRateData.length < 1;
    this.chartOptions = this.createChartOptions(statsSeries);
    this.renderChart();
  }

  private createChartOptions(series: any[]): any {
    return {
      chart: {
        type: 'line',
        zoomType: 'x',
        showAxes: true,
        height: 300
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false
          },
          turboThreshold: 2000
        }
      },
      tooltip: {
        shared: true
      },
      xAxis: {
        type: 'datetime',
        title: {
          enabled: false,
          text: 'Date and Time'
        }
      },
      yAxis: {
        title: {text: null},
        min: 0,
        max: 100,
        opposite: false,
        allowDecimals: true,
        id: '0',
        labels: {style: {color: 'black'}}
      },
      series,
      title: {
        text: null
      },
      accessibility: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: true
      }
    };
  }

  private renderChart(): void {
    if (!this.chartContainerRef?.nativeElement || !this.chartOptions || this.noData) {
      return;
    }

    this.destroyChart();
    this.chart = Highcharts.chart(this.chartContainerRef.nativeElement, this.chartOptions);
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
